import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { IPublicClientApplication } from '@azure/msal-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';
import { AppRouter } from './app.routes';
import { SecurityModule } from '@security/security.module';
// Top-level modules
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalGuard,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { DataApiModule } from '@data-api/data-api.module';
import { AppBackdropComponent } from '@observation/components/app-backdrop/app-backdrop.component';
import { ToastCustomComponent } from '@shared/components/toast-custom/toast-custom.component';
import { APP_CONFIG } from '@shared/config';
import { SharedModule } from '@shared/shared.module';
import { QuillModule } from 'ngx-quill';
import { sessionTokenInterceptor } from './interceptors/session-token/session-token.interceptor';
import { HttpErrorInterceptor } from './interceptors/http-error/http-error.interceptor';
import { MsalTokenInterceptor } from './interceptors/msal-token-interceptor/msal-token-interceptor';
import {
  initializeMsalInstance,
  msalGuardConfigFactory,
  msalInstanceFactory,
} from '@msal/msal.config';

import { appVersionInterceptor } from './interceptors/app-version/app-version.interceptor';
import { SvgSpriteLoaderService } from '@shared/services/svg-sprite-loader.service';

@NgModule({
  declarations: [AppComponent, ToastCustomComponent, AppBackdropComponent],
  // MsalRedirectComponent processes the MSAL redirect response at bootstrap
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    SharedModule.forRoot(),
    DataApiModule.forRoot(),
    AppRouter,
    ReactiveFormsModule,
    FormsModule,
    ToastrModule.forRoot({
      timeOut: 4000,
      progressBar: true,
      easing: 'ease-in',
      positionClass: 'toast-bottom-left',
      easeTime: 250,
      tapToDismiss: false,
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true,
      toastComponent: ToastCustomComponent,
    }),
    SecurityModule.forRoot(),
    QuillModule.forRoot(),
    MsalModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
      deps: [APP_CONFIG],
    },
    provideAppInitializer(() => {
      const msalInstance = inject<IPublicClientApplication>(MSAL_INSTANCE);
      return initializeMsalInstance(msalInstance)();
    }),
    provideAppInitializer(() => inject(SvgSpriteLoaderService).load()),
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalTokenInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    provideHttpClient(
      withInterceptors([appVersionInterceptor, sessionTokenInterceptor]),
      withInterceptorsFromDi(),
      withJsonpSupport()
    ),
  ],
})
export class AppModule {}
